import React from 'react';
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import { VALID_EMAIL_REGEX } from 'constants/email';
import Table from 'lib/Table/Table';
import { TableHeight } from 'lib/Table/Table.types';
import { PAGE_SIZE_DEFAULT } from 'consts/pagination';
import * as userActions from 'modules/api/user';
import Modal, { ModalSize } from 'lib/Modal';
import withCancelRequest from 'components/hocs/withCancelRequest';
import Button, { Color, Size } from 'lib/Button';
import Card from 'components/common/Card';
import { CardTheme, CardType } from 'components/common/Card/Card.types';
import Search from 'lib/Search';
import plusSvg from 'assets/icons/plus.svg';
import filterSvg from 'assets/icons/filter.svg';
import editSvg from 'assets/icons/edit.svg';
import removeSvg from 'assets/icons/remove.svg';
import userSvg from 'assets/icons/user.svg';
import debounce from 'lodash/debounce';
import User from '../User';
import { ActionRenderer, StatusRenderer, ViewRenderer } from '../GridCellRenderer';

class Users extends React.Component {
  constructor() {
    super();

    this.getUsersDebounced = debounce(this.getUsers, 300);
  }

  userTableApi;

  state = {
    filterUserText: '',
    isEditUserDialogActive: false,
    isDeleteUserDialogActive: false,
    selectedUser: {
      userId: '',
      name: '',
    },
  };

  componentDidUpdate = (_prevProps, prevState) => {
    const { filterUserText } = this.state;
    const isUserQueryValid =
      filterUserText.length > 2 || (prevState.filterUserText.length > 2 && filterUserText.length <= 2);

    if (isUserQueryValid) {
      this.getUsersDebounced();
    }
  };

  setUserTableApi = (tableApi) => {
    this.userTableApi = tableApi;
    this.userTableApi.setDatasource(this.userTableDataSource());
  };

  userTableDataSource = () => {
    const { cancelFunctions } = this.props;
    const { filterUserText } = this.state;
    return {
      getRows: async (params) => {
        const pageNu = parseInt(params.startRow / PAGE_SIZE_DEFAULT);
        try {
          const userResponse = await userActions.getUsers(cancelFunctions, filterUserText, pageNu, PAGE_SIZE_DEFAULT);
          params.successCallback(userResponse.users, userResponse.total);
        } catch {} // eslint-disable-line no-empty
      },
    };
  };

  onStatusChange = async (value, userData) => {
    const { cancelFunctions, addToastNotification } = this.props;
    try {
      const user = await userActions.changeUserStatus(cancelFunctions, userData.userId, !value);

      addToastNotification({
        description: `User ${user.name}'s status updated successfully.`,
        timeout: 2000,
        type: 'Success',
      });
      this.updateUserList(user);
      // eslint-disable-next-line no-empty
    } catch {}
  };

  updateUserList(user) {
    const itemsToUpdate = [];
    this.userTableApi.forEachNode((rowNode) => {
      const item = rowNode.data;
      if (item.userId === user.userId) {
        item.roles = user.roles;
        item.blocked = user.blocked;
        itemsToUpdate.push(item);
      }
    });
    this.userTableApi.updateRowData({ update: itemsToUpdate });
  }

  onEditUser = (userData) => {
    this.setState({
      selectedUser: userData,
      isEditUserDialogActive: true,
    });
  };

  onViewUserRole = async (userData) => {
    const { cancelFunctions } = this.props;
    try {
      const user = await userActions.getUserDetail(cancelFunctions, userData.userId);
      this.updateUserList(user);
      // eslint-disable-next-line no-empty
    } catch {}
  };

  onDeleteUser = (user) => {
    this.setState({
      selectedUser: user,
      isDeleteUserDialogActive: true,
    });
  };

  onConfirmDeleteUser = async (user) => {
    const { cancelFunctions, addToastNotification } = this.props;
    try {
      await userActions.deleteUser(cancelFunctions, user.userId);
      addToastNotification({ description: `User deleted successfully.`, timeout: 2000, type: 'Success' });
      this.userTableApi.refreshInfiniteCache();

      this.setState({
        isDeleteUserDialogActive: false,
      });
      // eslint-disable-next-line no-empty
    } catch {}
  };

  columnDefs = [
    {
      headerName: '',
      field: 'userId',
      minWidth: 70,
      maxWidth: 70,
      headerTooltip: 'Action',
      resizable: false,
      cellRenderer: ActionRenderer,
      cellRendererParams: {
        onClick: this.onEditUser,
        icon: editSvg,
        hasPermission: Auth.hasPermission(PermissionsEnum.USERS_EDIT),
      },
      hide: !Auth.hasPermission(PermissionsEnum.USERS_EDIT),
    },
    {
      headerName: 'Status',
      field: 'blocked',
      minWidth: 100,
      headerTooltip: 'Status',
      cellRenderer: StatusRenderer,
      cellRendererParams: {
        onStatusChange: this.onStatusChange,
      },
    },
    { headerName: 'Username', field: 'name', minWidth: 200, headerTooltip: 'Username' },
    { headerName: 'Email', field: 'email', minWidth: 200, headerTooltip: 'Email' },
    {
      headerName: 'Role',
      field: 'roles',
      cellRenderer: ViewRenderer,
      minWidth: 300,
      headerTooltip: 'Role',
      cellRendererParams: {
        onBtnClick: this.onViewUserRole,
      },
      resizable: false,
    },
    {
      headerName: '',
      field: 'userId',
      minWidth: 70,
      maxWidth: 70,
      headerTooltip: 'Action',
      resizable: false,
      cellRenderer: ActionRenderer,
      cellRendererParams: {
        onClick: this.onDeleteUser,
        icon: removeSvg,
        hasPermission: Auth.hasPermission(PermissionsEnum.USERS_DELETE),
      },
      hide: !Auth.hasPermission(PermissionsEnum.USERS_DELETE),
    },
  ];

  onCreateUser = () => {
    this.setState({
      selectedUser: {
        userId: '',
        name: '',
      },
      isEditUserDialogActive: true,
    });
  };

  onSaveUser = async (user) => {
    const { cancelFunctions, addToastNotification } = this.props;
    try {
      const validMsg = this.isValidUser(user);
      if (validMsg !== '') {
        addToastNotification({ description: validMsg, type: 'Failure' });
        return;
      }
      if (user.userId) {
        // update user
        await userActions.updateUser(cancelFunctions, user);
        this.userTableApi.refreshInfiniteCache();
        addToastNotification({ description: 'User details updated successfully.', timeout: 2000, type: 'Success' });
      } else {
        // create user
        const newUserObj = await userActions.createUser(cancelFunctions, user);
        if (newUserObj) {
          this.getUsers();
          addToastNotification({ description: 'User created successfully.', timeout: 2000, type: 'Success' });
        }
      }
      this.setState({
        isEditUserDialogActive: false,
      });
      // eslint-disable-next-line no-empty
    } catch {}
  };

  isValidUser = (user) => {
    let msg = '';
    if (!user.name || !user.email || (!user.userId && !user.password)) {
      msg = 'Please enter mandatory details.';
    } else if (!VALID_EMAIL_REGEX.test(user.email)) {
      msg = 'Please enter valid email address.';
    } else if (!user.userId && user.password.length < 8) {
      msg = 'Password must be at least 8 characters in length.';
    }
    return msg;
  };

  handleDialogToggle = (dialogName) => {
    const isDialogActive = this.state[dialogName];
    this.setState({
      [dialogName]: !isDialogActive,
    });
  };

  onFilterTextChange = (value) => {
    this.setState({ filterUserText: String(value ?? '') });
  };

  getUsers = () => {
    this.userTableApi.setDatasource(this.userTableDataSource());
  };

  render() {
    const { filterUserText, isEditUserDialogActive, isDeleteUserDialogActive, selectedUser } = this.state;

    return (
      <>
        <Modal
          isOpen={isEditUserDialogActive}
          icon={userSvg}
          title={selectedUser.userId ? 'Update user' : 'Create new user'}
          size={ModalSize.MEDIUM}
        >
          <User
            userId={selectedUser.userId}
            onSave={this.onSaveUser}
            onCancel={() => {
              this.handleDialogToggle('isEditUserDialogActive');
            }}
          />
        </Modal>
        <Modal
          isOpen={isDeleteUserDialogActive}
          icon={removeSvg}
          title="Delete user?"
          actionButtons={
            <>
              <Button
                color={Color.PRIMARY}
                onClick={() => {
                  this.handleDialogToggle('isDeleteUserDialogActive');
                }}
                size={Size.LARGE}
                label="Cancel"
              />
              <Button
                color={Color.DANGER}
                onClick={() => {
                  this.onConfirmDeleteUser(selectedUser);
                }}
                size={Size.LARGE}
                label="Delete"
              />
            </>
          }
        >
          <p className="ml-8 body-base text-neutral-950-opacity-60">
            Are you sure you want to delete <strong>{selectedUser.name}</strong>?
          </p>
        </Modal>
        <Card cardType={CardType.FILTERS} cardTheme={CardTheme.WHITE}>
          <div className="row flex items-center">
            <div className="col-1">
              <div className="horizontal-vertical-center">
                <SVG className="fill-neutral-400 w-5 h-5" src={filterSvg} />
              </div>
            </div>
            <div className="col-9">
              <Search
                value={filterUserText}
                onChange={this.onFilterTextChange}
                placeholder="Search user (type 3 letters)"
              />
            </div>
            <div className="col-12 col-md-2 flex justify-center">
              {Auth.hasPermission(PermissionsEnum.USERS_CREATE) && (
                <Button
                  onClick={this.onCreateUser}
                  color={Color.PRIMARY}
                  svg={plusSvg}
                  label="Create user"
                  size={Size.MEDIUM}
                />
              )}
            </div>
          </div>
        </Card>
        <div className="row">
          <div className="col-12">
            <Table
              columnDefs={this.columnDefs}
              getTableApi={this.setUserTableApi}
              extraGridOptions={{
                suppressScrollOnNewData: true,
                rowModelType: 'infinite',
                rowHeight: 60,
                infiniteInitialRowCount: 1,
                maxBlocksInCache: 1,
                pagination: true,
                paginationPageSize: PAGE_SIZE_DEFAULT,
                cacheBlockSize: PAGE_SIZE_DEFAULT,
                domLayout: 'autoHeight',
                enableCellTextSelection: true,
              }}
              tableHeight={TableHeight.AUTO}
            />
          </div>
        </div>
      </>
    );
  }
}

Users.propTypes = {
  cancelFunctions: PropTypes.objectOf(PropTypes.func).isRequired,
  addToastNotification: PropTypes.func.isRequired,
};

export default withCancelRequest(Users);
